/**
 * **工具节点包** —— 第一件"真干活"的节点：抓取网页内容。
 *
 * ## 它为什么是域包，而不是内核节点
 *
 * 内核那一层的规矩是**零 IO**（`node/` 里 `fetch` / `async` 命中数为 0）——
 * 那条规矩换来的是"求值层能整块搬进 Worker / QuickJS / 服务端"。
 * 抓网页是**副作用**，只能待在运行期，所以它是包：
 * 纯求值器（设计期给占位）+ runner（运行期真抓）都在包里，
 * 内核一个字节都不用改。
 *
 * ## 能力声明：`net`
 *
 * 节点类型上写 `capabilities: ['net']`，于是"这份文档需要什么宿主"
 * 由 `requirementOf` 推出来 —— 浏览器宿主有 `net` ✓（但**受 CORS 限制**，
 * 那条限制写在 `BROWSER_HOST.limits` 里，会显示在工具卡片上）。
 * 将来本机运行端也有 `net` ✓ 而且没有 CORS 问题 ✓ —— 同一份文档两边都能跑。
 *
 * ## ⚠️ 抓取要守规矩（这不是技术问题，是产品责任）
 *
 * 内置一层**限速**（同一主机两次请求之间至少间隔 `minIntervalMs`）。
 * 不做的话，这个节点就是一个"一键打爆别人网站"的工具，而后果挂在
 * 用这个产品的团队身上。所以：
 *   · 默认间隔（1 秒）写在包里，用户改不小（只能改大）；
 *   · 超时默认 10 秒 —— 不设超时的话，一次运行会挂着不走（取消都救不回来）。
 */
import type { NodePackage, NodeTypeDef } from '../../node/package'
import { EXTRACT_TYPE, extractEvaluator } from './extract'
import type { NodeEvaluator } from '../../node/evaluate'
import type { NodeRunner } from '../../node/runGraph'

/** 抓取结果（runner 的返回值 = 这个节点的输出端口值） */
export interface HttpResult {
  status: number
  body: string
  contentType: string
  /** 实际耗时（毫秒）—— "慢在哪一步"要看它 */
  ms: number
}

export interface ToolPackageOptions {
  /**
   * 真正发请求的实现。
   *
   * ⚠️ 做成**注入**是为了验证脚本能塞一个假的：抓真实网络既慢又不稳，
   * 而这一套要验的是"超时 / 取消 / 限速 / 错误文案"这些**我们自己的逻辑**。
   */
  fetch?: typeof fetch
  /** 覆盖默认限速（测试用；产品路径不许调小） */
  minIntervalMs?: number
  /** 覆盖默认超时 */
  defaultTimeoutMs?: number
}

/** 默认限速：同一主机两次请求之间至少 1 秒 */
const DEFAULT_MIN_INTERVAL_MS = 1000
/** 默认超时：10 秒 —— 不设的话一次运行会挂着不走 */
const DEFAULT_TIMEOUT_MS = 10_000

const HTTP_TYPE: NodeTypeDef = {
  type: 'tool.http',
  title: '工具 · 抓取网页',
  category: 'tool',
  width: 224,
  keywords: ['http', 'fetch', 'url', 'web', 'request', '抓取', '网页', '请求', '下载'],
  hint: '按网址取回内容（受 CORS 限制；本机运行端没有这个限制）',
  /* ★ 能力声明：宿主必须有 `net` 才能跑这份文档 */
  capabilities: ['net'],
  rows: [
    {
      kind: 'input',
      id: 'url',
      label: '网址',
      socket: { id: 'url', type: 'text' },
      widget: { id: 'url', kind: 'text', value: '', placeholder: 'https://example.com' },
    },
    {
      kind: 'input',
      id: 'timeoutMs',
      label: '超时',
      socket: { id: 'timeoutMs', type: 'number' },
      widget: { id: 'timeoutMs', kind: 'number', value: DEFAULT_TIMEOUT_MS, min: 200, max: 120_000, step: 100 },
    },
    { kind: 'output', id: 'status', label: '状态码', socket: { id: 'status', type: 'number' } },
    { kind: 'output', id: 'body', label: '正文', socket: { id: 'body', type: 'text' } },
    { kind: 'output', id: 'contentType', label: '类型', socket: { id: 'contentType', type: 'text' } },
    { kind: 'output', id: 'ms', label: '耗时', socket: { id: 'ms', type: 'number' } },
  ],
}

/**
 * 设计期：**空值**（和事件节点同一条规矩）。
 *
 * ⚠️ 设计期绝不发请求 ✗ —— 那会让"打开文档"变成"访问一堆网址"。
 * 空对象 = "算过了，但没有值"，下游因此回退到自己的内联默认值。
 */
export const httpDesignTime: NodeEvaluator = () => ({})

/** 一个主机上一次请求的时间戳（限速用） */
const lastHitAt = new Map<string, number>()

/** 等一会儿（限速；被取消就立刻抛） */
function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('已取消'))
      return
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = (): void => {
      clearTimeout(timer)
      reject(new Error('已取消'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

export function createToolPackage(options: ToolPackageOptions = {}): NodePackage {
  const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args))
  const minIntervalMs = Math.max(DEFAULT_MIN_INTERVAL_MS, options.minIntervalMs ?? 0)
  const defaultTimeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS

  const httpRunner: NodeRunner = async (api, context) => {
    const raw = api.input('url')
    const url = raw === undefined || raw === null ? '' : String(raw).trim()
    if (url === '') {
      throw new Error('网址是空的 —— 填一个地址，或者把上游接进来')
    }
    let parsed: URL
    try {
      parsed = new URL(url)
    } catch {
      throw new Error(`网址不合法：${url}`)
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new Error(`只支持 http / https，收到的是 ${parsed.protocol}`)
    }

    /* 限速：同一主机两次请求之间至少隔一会儿（见文件头那段） */
    const last = lastHitAt.get(parsed.host) ?? 0
    const waitMs = minIntervalMs - (Date.now() - last)
    if (waitMs > 0) await wait(waitMs, context.signal)
    lastHitAt.set(parsed.host, Date.now())

    const timeoutRaw = Number(api.input('timeoutMs'))
    const timeoutMs = Number.isFinite(timeoutRaw) && timeoutRaw >= 200 ? timeoutRaw : defaultTimeoutMs

    /* 超时与取消合成一个信号：两个都得能中断请求 */
    const local = new AbortController()
    const onAbort = (): void => local.abort()
    context.signal.addEventListener('abort', onAbort, { once: true })
    const timer = setTimeout(() => local.abort(), timeoutMs)
    const startedAt = Date.now()
    try {
      const response = await doFetch(url, { signal: local.signal, redirect: 'follow' })
      const body = await response.text()
      return {
        status: response.status,
        body,
        contentType: response.headers?.get?.('content-type') ?? '',
        ms: Date.now() - startedAt,
      }
    } catch (error) {
      /*
       * ⚠️ **错误文案要说人话**：浏览器里抓失败的绝大多数原因是 CORS，
       * 而 `fetch` 抛的是 `TypeError: Failed to fetch`（看不出原因）。
       * 不说清的话，用户会以为是这个节点坏了 —— 其实是浏览器的安全策略。
       */
      if (context.signal.aborted) throw new Error('已取消')
      if (local.signal.aborted) {
        throw new Error(`超时（${timeoutMs}ms 没响应）—— 地址不通，或者对方太慢`)
      }
      throw new Error(
        `抓取失败：${error instanceof Error ? error.message : String(error)}` +
          `（浏览器里最常见的原因是 CORS 不放行 —— 换成本机运行端就没有这个限制；` +
          `也可以先换一个允许跨域的地址试试）`,
      )
    } finally {
      clearTimeout(timer)
      context.signal.removeEventListener('abort', onAbort)
    }
  }

  return {
    id: 'tool',
    label: '工具',
    kind: 'domain',
    /*
     * ⚠️ 分类标签叫「网络」而不是「工具」：内核里已经有一个「工具」分类
     * （`utility`：框/注释/接通/循环这些**图自己的构造**）。两个分类同名的话，
     * 「添加节点」菜单里会出现两段「工具」—— 用户没法知道该去哪一段找。
     * 包名仍叫「工具」（它是工具箱），分类名要具体。
     */
    /* 分类叫「网页」：这一包的两件事都是围绕网页的 —— 抓回来 + 取出字段 */
    categories: [
      {
        id: 'tool',
        label: '网页',
        icon: '<circle cx="12" cy="12" r="8"/><path d="M4 12h16"/><path d="M12 4c2.6 3.2 2.6 12.8 0 16M12 4c-2.6 3.2-2.6 12.8 0 16"/>',
      },
    ],
    nodeTypes: [HTTP_TYPE, EXTRACT_TYPE],
    evaluators: {
      'tool.http': httpDesignTime,
      /* ★ 提取是**纯计算**：设计期就跑，编辑时当场看到结果（不是"跑了才知道"） */
      'tool.extract': extractEvaluator,
    },
    runners: { 'tool.http': httpRunner },
  }
}

/** 默认工具包（宿主用真 `fetch`） */
export const TOOL_PACKAGE: NodePackage = createToolPackage()
